import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Users,
  KeyRound,
  RefreshCw,
  Home,
  Trash2,
  Power,
  Copy,
  CalendarDays,
  ArrowLeft,
  Clock3,
} from "lucide-react";
import { showToast } from "../utils/toast";
import {
  buildCloudLoginUrl,
  clearInvalidCloudSession,
  isCloudSessionValidForHost,
} from "../utils/cloudBrowserSession";

const statusLabel = (status) => {
  if (!status || status === "pending") return "En attente";
  if (status === "active" || status === "connected") return "Connecté";
  if (status === "suspended") return "Suspendu";
  return status;
};

const badgeClass = (status) => {
  if (status === "active") return "bg-emerald-500/15 text-emerald-200 border border-emerald-300/40";
  if (status === "suspended") return "bg-red-500/15 text-red-200 border border-red-300/40";
  return "bg-amber-500/15 text-amber-200 border border-amber-300/40";
};

const toFrDate = (value, withTime = false) => {
  const iso = String(value || "").trim();
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return withTime
    ? date.toLocaleString("fr-FR")
    : date.toLocaleDateString("fr-FR");
};

const toDateInput = (value) => {
  const iso = String(value || "").trim();
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getFutureDateInput = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return toDateInput(d.toISOString());
};

const CloudDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useParams();
  const [tenants, setTenants] = useState([]);
  const [instances, setInstances] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ subdomain: "", type: "", locality: "" });
  const [licenseModal, setLicenseModal] = useState({
    open: false,
    action: "activate",
    tenant: null,
    expiresDate: getFutureDateInput(30),
    periodDays: 30,
    note: "",
    submitting: false,
  });
  const [tenantDetail, setTenantDetail] = useState(null);
  const [licenseHistory, setLicenseHistory] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const typeOptions = ["restaurant", "fastfood", "hotel", "bar"];

  const safeUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("moo_cloud_user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const token = useMemo(() => {
    try {
      return localStorage.getItem("moo_cloud_token") || "";
    } catch {
      return "";
    }
  }, []);

  const sessionAllowedOnCurrentHost = useMemo(
    () => isCloudSessionValidForHost({
      hostname: window.location?.hostname || "",
      user: safeUser,
      token,
    }),
    [safeUser, token],
  );

  useEffect(() => {
    if (sessionAllowedOnCurrentHost) return;
    clearInvalidCloudSession(window.localStorage);
    navigate(buildCloudLoginUrl({
      pathname: window.location?.pathname || "/",
      search: window.location?.search || "",
    }), { replace: true });
  }, [navigate, sessionAllowedOnCurrentHost]);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-store",
    }),
    [token],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("moo_cloud_user");
    localStorage.removeItem("moo_cloud_token");
    try {
      window.dispatchEvent(new Event("cloud-auth-changed"));
    } catch (_e) {
      // ignore
    }
    navigate("/cloud/login", { replace: true });
  }, [navigate]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await fetch("/api/cloud/tenants", { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setTenants(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.warn("Tenants fetch error", e);
    }
  }, [authHeaders]);

  const fetchInstances = useCallback(async () => {
    try {
      const res = await fetch("/api/cloud/instances", { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setInstances(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.warn("Instances fetch error", e);
    }
  }, [authHeaders]);

  const fetchTenantDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      setDetailLoading(true);
      const [tenantRes, historyRes] = await Promise.all([
        fetch(`/api/cloud/tenants/${id}`, { headers: authHeaders }),
        fetch(`/api/cloud/tenants/${id}/license-history`, { headers: authHeaders }),
      ]);
      if (!tenantRes.ok) throw new Error("Impossible de charger l’établissement");
      const tenantData = await tenantRes.json();
      const historyData = historyRes.ok ? await historyRes.json() : { history: [] };
      setTenantDetail(tenantData || null);
      setLicenseHistory(Array.isArray(historyData?.history) ? historyData.history : []);
    } catch (e) {
      console.warn("Tenant detail fetch error", e);
      showToast("error", e.message || "Erreur chargement client");
      setTenantDetail(null);
      setLicenseHistory([]);
    } finally {
      setDetailLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchTenants();
    fetchInstances();
  }, [fetchTenants, fetchInstances]);

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetail(tenantId);
    } else {
      setTenantDetail(null);
      setLicenseHistory([]);
    }
  }, [tenantId, fetchTenantDetail]);

  const createTenant = async () => {
    if (!form.subdomain.trim()) return;
    setLoading(true);
    try {
      const res = await fetch("/api/cloud/tenants", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subdomain: form.subdomain.trim(),
          type: form.type.trim() || null,
          locality: form.locality.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error || "Création impossible");
      }
      setForm({ subdomain: "", type: "", locality: "" });
      setShowModal(false);
      showToast("success", "Établissement créé");
      fetchTenants();
    } catch (e) {
      showToast("error", e.message || "Erreur création client");
    } finally {
      setLoading(false);
    }
  };

  const setSubdomain = async (tenantIdValue, currentSub) => {
    const sub = window.prompt("Sous-domaine ?", currentSub || "");
    if (!sub) return;
    try {
      const res = await fetch(`/api/cloud/tenants/${tenantIdValue}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subdomain: sub.trim() }),
      });
      if (!res.ok) throw new Error("Mise à jour impossible");
      showToast("success", "Sous-domaine mis à jour");
      fetchTenants();
      if (tenantIdValue === tenantId) fetchTenantDetail(tenantIdValue);
    } catch (e) {
      showToast("error", e.message || "Erreur mise à jour sous-domaine");
    }
  };

  const deleteTenant = async (tenantIdValue) => {
    const confirmDelete = window.confirm("Supprimer cet établissement du cloud ?");
    if (!confirmDelete) return;
    try {
      const res = await fetch(`/api/cloud/tenants/${tenantIdValue}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Suppression impossible");
      showToast("success", "Établissement supprimé");
      if (tenantIdValue === tenantId) {
        navigate("/cloud/clients");
      }
      fetchTenants();
    } catch (e) {
      showToast("error", e.message || "Erreur suppression établissement");
    }
  };

  const openLicenseModal = (tenant, action) => {
    setLicenseModal({
      open: true,
      action,
      tenant,
      expiresDate: toDateInput(tenant?.license_expires_at) || getFutureDateInput(30),
      periodDays: 30,
      note: "",
      submitting: false,
    });
  };

  const closeLicenseModal = () => {
    setLicenseModal((prev) => ({
      ...prev,
      open: false,
      submitting: false,
    }));
  };

  const submitLicenseAction = async () => {
    if (!licenseModal?.tenant?.id) return;
    const action = licenseModal.action === "deactivate" ? "deactivate" : "activate";
    const payload = { action };
    if (action === "activate") {
      if (!licenseModal.expiresDate) {
        showToast("error", "Date d’expiration requise");
        return;
      }
      payload.expires_at = new Date(`${licenseModal.expiresDate}T23:59:59`).toISOString();
      if (Number.isFinite(Number(licenseModal.periodDays)) && Number(licenseModal.periodDays) > 0) {
        payload.period_days = Number(licenseModal.periodDays);
      }
    }
    if (String(licenseModal.note || "").trim()) {
      payload.note = String(licenseModal.note).trim();
    }

    setLicenseModal((prev) => ({ ...prev, submitting: true }));
    try {
      const res = await fetch(`/api/cloud/tenants/${licenseModal.tenant.id}/license`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error || "Mise à jour licence impossible");
      }
      showToast(
        "success",
        action === "deactivate" ? "Licence désactivée" : "Licence activée/renouvelée",
      );
      closeLicenseModal();
      await fetchTenants();
      if (tenantId && tenantId === licenseModal.tenant.id) {
        await fetchTenantDetail(tenantId);
      }
    } catch (e) {
      showToast("error", e.message || "Erreur mise à jour licence");
      setLicenseModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const copyToClipboard = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        showToast("success", "Token copié");
        return;
      }
    } catch (_e) {
      // ignore
    }
    try {
      window.prompt("Copier le token :", text);
    } catch (_e) {
      // ignore
    }
  };

  const kpis = useMemo(() => {
    const total = tenants.length;
    const active = tenants.filter((t) => t.status === "active").length;
    const pending = tenants.filter((t) => t.status !== "active").length;
    const packages = new Map();
    tenants.forEach((t) => {
      const pkg = t.package_id || "Non défini";
      packages.set(pkg, (packages.get(pkg) || 0) + 1);
    });
    return [
      { label: "Clients", value: total, hint: "Total tenants" },
      { label: "Connectés", value: active, hint: "Statut active" },
      { label: "En attente", value: pending, hint: "Non activés" },
      { label: "Packages", value: packages.size, hint: "Types de formules" },
    ];
  }, [tenants]);

  const MenuButton = ({ id, label, icon: Icon, to }) => (
    <button
      onClick={() => navigate(to)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl ${
        activeTab === id ? "bg-amber-400 text-slate-900 font-semibold" : "bg-white/0 text-white/80"
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );

  useEffect(() => {
    if (location.pathname.includes("/cloud/clients")) setActiveTab("clients");
    else if (location.pathname.includes("/cloud/activations")) setActiveTab("activations");
    else setActiveTab("dashboard");
  }, [location.pathname]);

  const renderDashboard = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <p className="text-xs text-white/60">{k.label}</p>
            <p className="text-2xl font-bold">{k.value}</p>
            <p className="text-xs text-white/50">{k.hint}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Santé des installations</h3>
          <button
            onClick={fetchInstances}
            className="text-xs rounded-full border border-white/15 px-3 py-1 text-white/80 hover:border-amber-400/60"
          >
            <RefreshCw size={14} className="inline mr-1" />
            Rafraîchir
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {instances.length === 0 && (
            <p className="text-white/60 text-xs">Aucune installation active pour le moment.</p>
          )}
          {instances.map((inst) => (
            <div key={inst.id} className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{inst.name}</p>
                  <p className="text-xs text-white/60">{inst.subdomain}.moosync.com</p>
                </div>
                <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badgeClass(inst.status)}`}>
                  {inst.status || "pending"}
                </span>
              </div>
              <p className="text-xs text-white/60">
                Dernier ping : {inst.last_ping ? new Date(inst.last_ping).toLocaleString("fr-FR") : "—"}
              </p>
              <p className="text-xs text-white/60">Version : {inst.version || "—"} · Uptime : {inst.uptime || "—"}</p>
              <p className="text-xs text-white/60">Alertes : {inst.alerts ?? 0}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderClients = () => (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Clients</p>
          <h3 className="text-lg font-semibold">Liste des établissements</h3>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left text-white/80">
          <thead className="text-xs uppercase text-white/60 border-b border-white/10">
            <tr>
              <th className="px-3 py-2">Nom</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Localité</th>
              <th className="px-3 py-2">Sous-domaine</th>
              <th className="px-3 py-2">Token</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2">Expiration</th>
              <th className="px-3 py-2">Enregistré</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {tenants.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-3 text-center text-white/60">
                  Aucun client pour le moment.
                </td>
              </tr>
            )}
            {tenants.map((t) => (
              <tr
                key={t.id}
                className="hover:bg-white/5 cursor-pointer"
                onClick={() => navigate(`/cloud/clients/${t.id}`)}
              >
                <td className="px-3 py-2">{t.name}</td>
                <td className="px-3 py-2">{t.type || "—"}</td>
                <td className="px-3 py-2">{t.locality || "—"}</td>
                <td className="px-3 py-2">{t.subdomain ? `${t.subdomain}.moosync.com` : "—"}</td>
                <td className="px-3 py-2">
                  {t.token ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        copyToClipboard(t.token);
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1 text-xs text-white/70 hover:border-amber-400/60 hover:text-white transition"
                      title="Copier le token"
                    >
                      <Copy size={12} />
                      Copier
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badgeClass(t.status)}`}>
                    {statusLabel(t.status)}
                  </span>
                </td>
                <td className="px-3 py-2 text-white/60">{toFrDate(t.license_expires_at)}</td>
                <td className="px-3 py-2 text-white/60">{toFrDate(t.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (t.status === "suspended") {
                          openLicenseModal(t, "activate");
                        } else {
                          openLicenseModal(t, "deactivate");
                        }
                      }}
                      className={`p-2 rounded-full border transition ${
                        t.status === "suspended"
                          ? "border-emerald-400/40 text-emerald-200 hover:border-emerald-300/70"
                          : "border-red-400/40 text-red-200 hover:border-red-300/70"
                      }`}
                      title={t.status === "suspended" ? "Activer" : "Désactiver"}
                    >
                      <Power size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteTenant(t.id);
                      }}
                      className="p-2 rounded-full border border-white/10 text-white/70 hover:border-red-400/60 hover:text-red-300 transition"
                      title="Supprimer l'établissement"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderClientDetail = () => (
    <div className="space-y-4">
      {detailLoading ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-sm text-white/70">
          Chargement des informations client...
        </div>
      ) : null}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/cloud/clients")}
              className="rounded-full border border-white/15 p-2 text-white/80 hover:border-amber-400/60"
              title="Retour"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Client</p>
              <h3 className="text-xl font-semibold">
                {tenantDetail?.name || "Établissement"}
              </h3>
              <p className="text-sm text-white/70">
                {tenantDetail?.subdomain ? `${tenantDetail.subdomain}.moosync.com` : "—"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badgeClass(tenantDetail?.status)}`}>
              {statusLabel(tenantDetail?.status)}
            </span>
            <button
              type="button"
              onClick={() => {
                if (tenantDetail?.status === "suspended") {
                  openLicenseModal(tenantDetail, "activate");
                } else {
                  openLicenseModal(tenantDetail, "deactivate");
                }
              }}
              className={`p-2 rounded-full border transition ${
                tenantDetail?.status === "suspended"
                  ? "border-emerald-400/40 text-emerald-200 hover:border-emerald-300/70"
                  : "border-red-400/40 text-red-200 hover:border-red-300/70"
              }`}
              title={tenantDetail?.status === "suspended" ? "Activer" : "Désactiver"}
            >
              <Power size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Date d’enregistrement</div>
            <div className="font-semibold mt-1">{toFrDate(tenantDetail?.created_at, true)}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Date d’expiration</div>
            <div className="font-semibold mt-1">{toFrDate(tenantDetail?.license_expires_at, true)}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Révision licence</div>
            <div className="font-semibold mt-1">
              {Number.isFinite(Number(tenantDetail?.license_revision))
                ? Number(tenantDetail.license_revision)
                : "—"}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold">Historique des renouvellements</h4>
          <button
            type="button"
            onClick={() => tenantId && fetchTenantDetail(tenantId)}
            className="text-xs rounded-full border border-white/15 px-3 py-1 text-white/80 hover:border-amber-400/60"
          >
            <RefreshCw size={13} className="inline mr-1" />
            Rafraîchir
          </button>
        </div>
        {licenseHistory.length === 0 ? (
          <p className="text-sm text-white/60">Aucun renouvellement enregistré.</p>
        ) : (
          <div className="space-y-2">
            {licenseHistory.map((item) => (
              <div key={item.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 text-sm font-semibold">
                    <Clock3 size={14} className="text-amber-300" />
                    {String(item.action || "—")}
                  </div>
                  <div className="text-xs text-white/60">{toFrDate(item.created_at, true)}</div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-white/70">
                  <div>
                    <span className="text-white/50">Statut</span>
                    <div>{String(item.previous_status || "—")} → {String(item.new_status || "—")}</div>
                  </div>
                  <div>
                    <span className="text-white/50">Expiration</span>
                    <div>{toFrDate(item.previous_expires_at)} → {toFrDate(item.new_expires_at)}</div>
                  </div>
                  <div>
                    <span className="text-white/50">Auteur</span>
                    <div>{item.actor_name || item.actor_type || "—"}</div>
                  </div>
                </div>
                {item.note ? <p className="mt-2 text-xs text-white/70">Note: {item.note}</p> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderActivations = () => (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Activations</p>
            <h3 className="text-lg font-semibold">Tokens & statuts</h3>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="rounded-2xl px-3 py-2 bg-amber-400 text-slate-900 text-sm font-semibold"
          >
            Nouveau token
          </button>
        </div>
        <div className="space-y-2 text-sm text-white/80 max-h-96 overflow-y-auto">
          {tenants.length === 0 && <p className="text-white/60 text-xs">Aucune activation pour le moment.</p>}
          {tenants.map((t) => (
            <div key={t.id} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 space-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-xs text-white/60">{t.subdomain ? `${t.subdomain}.moosync.com` : "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badgeClass(t.status)}`}>
                    {statusLabel(t.status)}
                  </span>
                  <button
                    type="button"
                    onClick={() => (t.status === "suspended" ? openLicenseModal(t, "activate") : openLicenseModal(t, "deactivate"))}
                    className={`p-2 rounded-full border transition ${
                      t.status === "suspended"
                        ? "border-emerald-400/40 text-emerald-200 hover:border-emerald-300/70"
                        : "border-red-400/40 text-red-200 hover:border-red-300/70"
                    }`}
                    title={t.status === "suspended" ? "Activer" : "Désactiver"}
                  >
                    <Power size={14} />
                  </button>
                  <button
                    onClick={() => deleteTenant(t.id)}
                    className="p-2 rounded-full border border-white/10 text-white/70 hover:border-red-400/60 hover:text-red-300 transition"
                    title="Supprimer l'établissement"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/60">
                <span>Token :</span>
                {t.token ? (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(t.token)}
                    className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:border-amber-400/60 hover:text-white transition"
                    title="Copier le token"
                  >
                    <Copy size={12} />
                    Copier
                  </button>
                ) : (
                  <span>—</span>
                )}
              </div>
              <p className="text-xs text-white/60">
                Type : {t.type || "—"} · Localité : {t.locality || "—"} · Expire : {toFrDate(t.license_expires_at)}
              </p>
              {t.status === "active" && (
                <div className="flex items-center gap-2">
                  <button
                    className="text-xs rounded-full border border-white/15 px-3 py-1 text-white/80 hover:border-amber-400/60"
                    onClick={() => setSubdomain(t.id, t.subdomain)}
                  >
                    Définir le sous-domaine
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050b18] via-[#0b152d] to-[#0a1224] text-white flex">
      <aside className="w-64 border-r border-white/10 bg-white/5 backdrop-blur-xl p-4 hidden md:block">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Cloud</p>
          <div className="mt-2 flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
              <img
                src="/moo-sync-insigne-logo.png"
                alt="MOO Sync"
                className="h-5 w-5 object-contain"
              />
            </div>
            <h2 className="text-lg font-semibold">Superadmin</h2>
          </div>
          <p className="text-xs text-white/60">{safeUser.name || "Djizef SARL"}</p>
        </div>
        <nav className="space-y-2">
          <MenuButton id="dashboard" to="/cloud/dashboard" label="Tableau de bord" icon={Home} />
          <MenuButton id="clients" to="/cloud/clients" label="Clients" icon={Users} />
          <MenuButton id="activations" to="/cloud/activations" label="Activations" icon={KeyRound} />
        </nav>
        <button
          onClick={logout}
          className="mt-6 w-full rounded-xl border border-white/15 px-3 py-2 text-sm text-white/80 hover:border-amber-400/60 transition"
        >
          Déconnexion
        </button>
      </aside>

      <main className="flex-1 px-4 md:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between md:hidden">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Cloud</p>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-9 w-9 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
                <img
                  src="/moo-sync-insigne-logo.png"
                  alt="MOO Sync"
                  className="h-5 w-5 object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold">Console</h1>
            </div>
          </div>
          <button
            onClick={logout}
            className="rounded-2xl border border-white/15 px-3 py-2 text-sm text-white/80 hover:border-amber-400/60 transition"
          >
            Déconnexion
          </button>
        </div>
        {activeTab === "dashboard" && renderDashboard()}
        {activeTab === "clients" && (tenantId ? renderClientDetail() : renderClients())}
        {activeTab === "activations" && renderActivations()}
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="max-w-md w-full rounded-3xl bg-white/5 border border-white/10 p-6 space-y-4 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Nouvelle activation</p>
                <h3 className="text-xl font-semibold">Créer un token client</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-sm"
              >
                Fermer
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-white/80">Sous-domaine souhaité</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={form.subdomain}
                    onChange={(e) => setForm((p) => ({ ...p, subdomain: e.target.value }))}
                    className="flex-1 rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                    placeholder="ex: restocoffee"
                  />
                  <span className="text-sm text-white/60 pr-2">.moosync.com</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-white/80">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                  >
                    <option value="">Sélectionner</option>
                    {typeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/80">Localité</label>
                  <input
                    type="text"
                    value={form.locality}
                    onChange={(e) => setForm((p) => ({ ...p, locality: e.target.value }))}
                    className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                    placeholder="Ville / Pays"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-2xl border border-white/15 px-4 py-2 text-sm text-white/80"
              >
                Annuler
              </button>
              <button
                disabled={loading}
                onClick={createTenant}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold px-4 py-2 shadow disabled:opacity-60"
              >
                {loading ? "Création..." : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {licenseModal.open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="max-w-lg w-full rounded-3xl bg-white/5 border border-white/10 p-6 space-y-4 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Licence client</p>
                <h3 className="text-xl font-semibold">
                  {licenseModal.action === "deactivate" ? "Désactiver l’abonnement" : "Activer / renouveler"}
                </h3>
                <p className="text-sm text-white/70 mt-1">{licenseModal.tenant?.name || "—"}</p>
              </div>
              <button
                onClick={closeLicenseModal}
                disabled={licenseModal.submitting}
                className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-sm disabled:opacity-60"
              >
                Fermer
              </button>
            </div>

            {licenseModal.action === "deactivate" ? (
              <div className="rounded-2xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                Cette action bloque immédiatement la connexion de l’établissement.
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-white/80 flex items-center gap-2">
                    <CalendarDays size={14} />
                    Date d’expiration
                  </label>
                  <input
                    type="date"
                    value={licenseModal.expiresDate}
                    onChange={(event) =>
                      setLicenseModal((prev) => ({
                        ...prev,
                        expiresDate: event.target.value,
                        periodDays: null,
                      }))
                    }
                    className="mt-1 w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[30, 60, 90].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() =>
                        setLicenseModal((prev) => ({
                          ...prev,
                          periodDays: days,
                          expiresDate: getFutureDateInput(days),
                        }))
                      }
                      className={`rounded-2xl px-3 py-2 text-xs font-semibold border transition ${
                        Number(licenseModal.periodDays) === days
                          ? "border-amber-400/70 bg-amber-400/15"
                          : "border-white/15 bg-white/5 hover:border-amber-300/50"
                      }`}
                    >
                      {days} jours
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-white/80">Note (optionnel)</label>
              <textarea
                value={licenseModal.note}
                onChange={(event) =>
                  setLicenseModal((prev) => ({ ...prev, note: event.target.value }))
                }
                rows={2}
                className="mt-1 w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40 resize-none"
                placeholder="Ex: renouvellement mensuel"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={closeLicenseModal}
                disabled={licenseModal.submitting}
                className="rounded-2xl border border-white/15 px-4 py-2 text-sm text-white/80 disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                disabled={licenseModal.submitting}
                onClick={submitLicenseAction}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  licenseModal.action === "deactivate"
                    ? "bg-gradient-to-r from-red-500 to-rose-500 text-white"
                    : "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900"
                } disabled:opacity-60`}
              >
                {licenseModal.submitting
                  ? "Traitement..."
                  : licenseModal.action === "deactivate"
                    ? "Confirmer la désactivation"
                    : "Confirmer l’activation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudDashboard;
